import { ConsoleLogger } from 'aws-amplify/utils';
import {
    Form as FormikForm,
    Formik,
    FormikComputedProps,
    FormikHandlers,
    FormikRegistration,
    FormikSharedConfig,
    FormikState,
    Field,
    ErrorMessage
} from "formik";
import React, {JSX} from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    FormLabel,
    OverlayTrigger,
    Popover,
    Row,
} from "react-bootstrap";
import {
    ColumnDef,
} from "@tanstack/react-table";
import {v4 as uuidv4} from 'uuid';
import * as yup from "yup";
import {DataAuditEvent} from "../../CommonApi/DataAudit";
import {EventType, FrontendModule} from "../../CommonApi/Enum";
import {TableDefinition, TableMetadata} from "../../CommonApi/TableMetadata";
import {UsageTracking} from "../../CommonApi/UsageTracking";
import {View} from "../GenGenPlate/Enum";
import {genGenPlateApiConst} from "../GenGenPlate/GenGenPlateApiConst";
import {RowDataModal} from "../RowDataModal";
import {StringFormatter} from "../../CommonFunc/StringFormatter";
import "../GenGenPlate/RawData/GenGenPlateCalculatedResult.css"
import {TableCellFormater} from "../../CommonFunc/TableCellFormater";
import {ROW_LIMIT} from "./RowLimit";
import {FormikHelpers} from "formik/dist/types";
import {TanStackTableWithUpdate} from "./TanStackUpdateTable";
import {SearchBoxComparator} from "../../CommonFunc/Enum";

const logger = new ConsoleLogger("SearchResultTableWithUpdateUI.tsx")

interface SearchResultTableWithUpdateUIProps {
    title: string
    databaseName: string
    tableName: string
    keywords: string
    columns: string
    keyField: string
    legends: any
    rowClasses: ((row: any, rowIndex: number) => string)
    refreshToken: string
    columnsDisplayInOrder: string[]
    columnsHidden: string[]
    view: View
    displayPagination?: boolean
    enableFilter?: boolean
    enableSorting?: boolean
    operator: string
    updateModalFullscreen: true | string
    modalClassName: string
    modalForm: React.ComponentClass<any, any>
    tableData: any[]
    columnsDefinitions: Record<string, string>
    unsearchableColumns: string[]
    columnsDescriptions: any[]
    tableDescription: string
    searchTable: (keywords: string, columns: string, table: string, comparator: string) => Promise<any[]>
}

interface SearchResultTableWithUpdateUIState {
    tableDefinitionStatus: "loaded" | "loading" | "error"
    tableDefinitionErrorMessage: string
    tableDescription: string

    columnsDescriptions: any[]
    columnsDefinitions: Record<string, string>
    columnsMetadataStatus: "loaded" | "loading" | "error"
    columnsMetadataErrorMessage: string

    tableData: any[]
    tableDataStatus: "loading" | "error" | "loaded"
    tableDataErrorMessage: string
    formikInitialState: {
        keywords: string
        columns: string
        operator: string
    }
    updateFormShow: boolean
    updateFormInitialValues: any
    keywords: string
    columns: string
    operator: string
}

const WIDE_COLUMNS: string[] = ["additionalNote", "name", "crisprEditedGeneKnockoutLinesGenerated", "media"]

export class SearchResultTableWithUpdateUI extends React.Component<
    SearchResultTableWithUpdateUIProps,
    SearchResultTableWithUpdateUIState
> {
    public static defaultProps = {
        comparator: "like",
        updateModalFullscreen: "",
        tableData: [],
        tableDescription: "",
        columnsDefinitions: {},
        columnsDescriptions: [],
        unsearchableColumns: [],
        modalClassName: "",
    }

    // This is to call formik helper func outside formik, like resetForm, etc.
    private readonly formikRef: React.RefObject<(FormikSharedConfig<{}> & FormikState<{
        keywords: string;
        columns: string;
        operator: string;
    }> & FormikHelpers<{ keywords: string; columns: string; operator: string }> & FormikHandlers & FormikComputedProps<{
        keywords: string;
        columns: string;
        operator: string;
    }> & FormikRegistration & { submitForm: () => Promise<any> }) | null>;

    constructor(props: SearchResultTableWithUpdateUIProps) {
        super(props);
        this.formikRef = React.createRef()
        this.state = {
            tableDefinitionStatus: "loaded",
            tableDefinitionErrorMessage: "",
            tableDescription: "",
            columnsDescriptions: [{dataField: "loading", text: "loading"}],
            columnsMetadataStatus: "loaded",
            columnsMetadataErrorMessage: "",
            columnsDefinitions: {},
            tableData: [],
            tableDataStatus: "loaded",
            tableDataErrorMessage: "",
            formikInitialState: {
                columns: "",
                keywords: "",
                operator: "",
            },
            updateFormShow: false,
            updateFormInitialValues: {},
            keywords: props.keywords,
            columns: props.columns,
            operator: props.operator,
        }
    }

    async componentDidMount() {
        this.getTableDefinition(this.props.databaseName, this.props.tableName)
        await this.getColumnsDefinitions(this.props.databaseName, this.props.tableName)
        if (this.props.keywords !== "") {
            this.applyFilter(false, () => {})
        }
    }

    componentDidUpdate(prevProps: Readonly<SearchResultTableWithUpdateUIProps>) {
        if (prevProps.refreshToken !== this.props.refreshToken) {
            logger.debug(`Refresh token changed from ${prevProps.refreshToken} to ${this.props.refreshToken}`)
            // If refresh token change, mean that the search query must come from outside the table,
            // hence we reset the form "Search table by"
            UsageTracking.recordEvent({
                module: FrontendModule.DataCatalog,
                event: this.props.view === View.Update ? EventType.SearchBeforeUpdate : EventType.Search,
                table: this.props.tableName,
                database: this.props.databaseName,
                keywords: this.state.keywords, // Comma seperated keywords is split to separate Cloudwatch log at backend.
            })
            this.setState(prevState => ({
                ...prevState,
                formikInitialState: {
                    ...prevState.formikInitialState,
                    columns: this.state.columnsDescriptions[0]?.dataField || "",
                    keywords: "",
                },
                keywords: this.props.keywords,
                columns: this.props.columns,
                operator: this.props.operator,
            }), () => {
                this.applyFilter(false, () => {})
            })
        }
    }

    applyFilter(searchInside: boolean, callback: () => void) {
        // We no longer need to resetForm, since we already delete the per table search form so Search/Update UI will always have just one search box.
        // if (!searchInside && this.formikRef.current) {
        //     this.formikRef.current.resetForm()
        // }

        if (Object.keys(this.props.tableData).length > 0) {
            this.setState(prevState => ({
                ...prevState,
                tableData: this.props.tableData,
                tableDataStatus: "loaded",
                tableDataErrorMessage: "",
            }))
            return
        }

        this.setState(prevState => ({
            ...prevState,
            tableDataStatus: "loading",
            updateFormShow: false,
        }), () => {
            // UsageTracking.recordEvent cannot be here, since this doesn't always trigger because people want to search,
            // it can trigger because ppl close the modal and table refresh.

            // Search only non-hidden columns, otherwise user may see data that doesn't contains searched keywords and assume faulty UI.
            let searchableColumns: string[]
            if (this.state.columns.trim() === "") {
                // When columns is empty, build searchable columns from columnsDescriptions, excluding hidden columns
                searchableColumns = this.state.columnsDescriptions
                    .filter((column: any) => !column.hidden)
                    .map((column: any) => column.dataField)
            } else {
                searchableColumns = this.state.columns.split(",")
                searchableColumns = searchableColumns.filter((column: string) => !this.props.columnsHidden.includes(column))
            }
            this.props.searchTable(this.state.keywords, searchableColumns.join(","), this.props.tableName, this.state.operator)
                .then((response: DataAuditEvent[]) => {
                    this.setState(prevState => ({
                        ...prevState,
                        tableData: response,
                        tableDataStatus: "loaded",
                        tableDataErrorMessage: ""
                    }))
                }).catch((err: any) => {
                    logger.error(err)
                    this.setState({
                        tableDataStatus: "error",
                        tableDataErrorMessage: "Something wrong happened, please refresh the page."
                    })
                }).finally(callback)
            return
        })
    }

    // Display column definition if hover over column name
    columnHeaderFormatter(columnId: string): JSX.Element {
        let columnText = StringFormatter.camelCaseToSpaceSeperatedString(columnId)
        return <OverlayTrigger
            delay={{show: 250, hide: 400}}
            placement={"top"}
            overlay={
                <Popover className={"ms-2"}
                    id={`${this.props.tableName}-${columnId}-definition`}>
                    <Popover.Header as={"h3"}>Definition</Popover.Header>
                    <Popover.Body>
                        {this.state.columnsDefinitions[columnId] ? this.state.columnsDefinitions[columnId] : columnText}
                    </Popover.Body>
                </Popover>
            }>
            <Row>
                <Col md={12} sm={12}>
                    <label htmlFor={`${this.props.tableName}-${columnId}-definition`}>
                        {columnText}
                    </label>
                </Col>
            </Row>
        </OverlayTrigger>
    }

    getColumnsDefinitions(databaseName: string, tableName: string): Promise<void> {
        this.setState(prevState => ({
            ...prevState,
            columnsMetadataStatus: "loading"
        }))
        return new Promise((resolve, reject) => {
            TableMetadata.getColumnsDefinitions(
                tableName, databaseName,
                (columnsDef: Record<string, string>) => {
                    let columnsDescriptions: any[] = []
                    for (const columnNameFromDb in columnsDef) {
                        if (WIDE_COLUMNS.includes(columnNameFromDb)) {
                            columnsDescriptions.push({
                                dataField: columnNameFromDb,
                                hidden: this.props.columnsHidden.includes(columnNameFromDb),
                                headerFormatter: this.columnHeaderFormatter.bind(this),
                                headerStyle: {width: "50rem"}
                            })
                        } else {
                            columnsDescriptions.push({
                                dataField: columnNameFromDb,
                                hidden: this.props.columnsHidden.includes(columnNameFromDb),
                                formatter: TableCellFormater.cellFormatter,
                                // Display column definition if hover over column name
                                headerFormatter: this.columnHeaderFormatter.bind(this)
                            })
                        }
                    }
                    logger.debug("columnsDescriptions", columnsDescriptions)
                    columnsDescriptions = this.sortColumnsInOrder(columnsDescriptions, this.props.columnsDisplayInOrder)

                    logger.info("sorted for table " + tableName + " columnsDescriptions = ", columnsDescriptions)
                    this.setState(prevState => ({
                        ...prevState,
                        columnsDefinitions: columnsDef,
                        columnsDescriptions: columnsDescriptions,
                        columnsMetadataStatus: "loaded",
                        columnsMetadataErrorMessage: "",
                    }), () => resolve())
                },
                (err: any) => {
                    this.setState({
                        columnsMetadataStatus: "error",
                        columnsMetadataErrorMessage: err+""
                    })
                    reject(err)
                }
            )
        })
    }

    getTableDefinition(databaseName: string, tableName: string) {
        this.setState(prevState => ({
            ...prevState,
            tableDefinitionStatus: "loading"
        }), () => {
            // Not user-initiated action, no need to UsageTracking.recordEvent
            TableMetadata.getTableDefinition(
                tableName, databaseName,
                (tableDef: TableDefinition) => {
                    this.setState(prevState => ({
                        ...prevState,
                        tableDescription: tableDef.description,
                        tableDefinitionStatus: "loaded",
                        tableDefinitionErrorMessage: ""
                    }))
                },
                (err: any) => {
                    this.setState({
                        tableDefinitionStatus: "error",
                        tableDefinitionErrorMessage: err + ""
                    })
                }
            )
        })
    }

    sortColumnsInOrder(columnsFromDb: any[], columnsOrder: string[]): any[] {
        let sortedColumns: any[] = []
        for (const columnName of columnsOrder) {
            for (const columnFromDb of columnsFromDb) {
                if (columnFromDb.dataField === columnName) {
                    sortedColumns.push(columnFromDb)
                    break;
                }
            }
        }
        logger.info("sortedColumns = ", sortedColumns)

        // Need to insert the rest.
        let unimportantColumn: any[] = columnsFromDb.filter(x => !sortedColumns.includes(x))
        sortedColumns.push(...unimportantColumn)

        return sortedColumns
    }

    buildColumnDefinitions(): ColumnDef<any>[] {
        const columns = this.state.columnsDescriptions.map((elem) => {
            return {
                id: elem.dataField,
                accessorKey: elem.dataField,
                header: () => this.columnHeaderFormatter(elem.dataField),
                cell: (info: any) => {
                    const value = info.getValue()
                    const row = info.row.original
                    return TableCellFormater.cellFormatter(value, row, info.row.index, info.column.id) ?? null
                },
                enableSorting: elem.sort !== false,
                enableFilter: this.props.enableFilter,
                meta: {
                    hidden: elem.hidden,
                    // Set minimum width for text-heavy columns
                    width: WIDE_COLUMNS.includes(elem.dataField) ? "18rem" : elem.headerStyle?.width || "9rem",
                    definition: this.state.columnsDefinitions[elem.dataField],
                }
            }
        })

        // Add action column for Update
        if (this.props.view === View.Update || this.props.tableName === genGenPlateApiConst.tableName.AGGREGATED) {
            columns.unshift({
                accessorKey: undefined,
                id: 'action',
                header: (): JSX.Element => <span>Action</span>,
                cell: info => {
                    if (!info?.row?.original) return null
                    return (
                        <Button size={"sm"} variant={"primary"} onClick={() => {
                            // TODO - how to decouple?
                            if (this.props.tableName === genGenPlateApiConst.tableName.AGGREGATED) {
                                UsageTracking.recordEvent({
                                    uuid: uuidv4(),
                                    module: FrontendModule.DataCatalog,
                                    event: EventType.ViewGenGenPlate,
                                    table: this.props.tableName,
                                    database: this.props.databaseName,
                                    keywords: "",
                                    timestamp: new Date()
                                })
                            }
                            this.setState({
                                updateFormShow: true,
                                updateFormInitialValues: info.row.original,
                            })
                        }}>
                            {this.props.view === View.Read ? "View" : "Update"}
                        </Button>
                    )
                },
                enableSorting: false,
                enableFilter: this.props.enableFilter,
                meta: {definition: '', hidden: undefined, width: undefined},
            })
        }
        return columns
    }

    closeModal() {
        this.setState({
            updateFormShow: false
        }, () => {
            // Only refresh table if this is update view, in read view no data change, so no need to refresh table.
            if (this.props.view === View.Update) {
                // FIXME - how to keep values in Formik, without resetForm?
                this.applyFilter(false, () => {})
            }
        })
    }

    render() {
        logger.debug("render - ", this.props.tableName, " - tableData = ", this.state.tableData)

        return <>
            {
                (this.props.view === View.Update || this.props.view === View.Read) && <RowDataModal
                    show={this.state.updateFormShow}
                    tableName={this.props.tableName}
                    initialValues={this.state.updateFormInitialValues}
                    closeModal={this.closeModal.bind(this)}
                    view={this.props.view}
                    modalFullscreen={this.props.updateModalFullscreen}
                    modalClassName={this.props.modalClassName}
                    modalForm={this.props.modalForm}
                />
            }
            {this.props.title !== "" && <h3 className={"text-primary mt-3"}>{this.props.title}</h3>}
            <Card className="bg-light mb-3">
                <Card.Header>
                    <Row>
                        <Col md={12}>
                            {this.props.enableFilter && <div className={"p-2"}>
                                <Formik
                                    // Allow calling of formik helpers outside formik
                                    innerRef={this.formikRef}
                                    enableReinitialize
                                    initialValues={this.state.formikInitialState}
                                    validationSchema={yup.object({
                                        columns: yup.string().required(),
                                        keywords: yup.string().required(),
                                        operator: yup.string().required(),
                                    })}
                                    onSubmit={(values, {setSubmitting, setFieldError}) => {
                                        setSubmitting(true)
                                        logger.debug("Submit filter - values = ", values)
                                        UsageTracking.recordEvent({
                                            module: FrontendModule.DataCatalog,
                                            event: this.props.view === View.Update ? EventType.SearchBeforeUpdate : EventType.Search,
                                            table: this.props.tableName,
                                            database: this.props.databaseName,
                                            keywords: this.state.keywords, // Comma seperated keywords is splited to seperate Cloudwatch log at backend.
                                        })

                                        // Check if the keywords contains / --> most likely search for date --> ask user to use dash instead
                                        let keywords = values["keywords"]
                                        if (/[0-9]{1,4}\//.test(keywords)) {
                                            setFieldError("keywords", "Avoiding using \"/\" when searching for date, date format is YYYY-MM-DD hh:mm")
                                            setSubmitting(false)
                                            return
                                        }

                                        this.setState(
                                            {
                                                keywords: keywords,
                                                columns: values["columns"],
                                                operator: values["operator"],
                                            }, () => {
                                                this.applyFilter(true,
                                                    () => {
                                                        setSubmitting(false)
                                                        // Do not resetForm,
                                                        // since user may want to keep search for same column
                                                        // for different keywords
                                                        // resetForm()
                                                        return
                                                    }
                                                )
                                            })
                                    }}
                                >
                                    {({
                                          values,
                                          errors,
                                          touched,
                                          handleChange,
                                          handleBlur,
                                          handleSubmit,
                                          isSubmitting}) => (
                                        <FormikForm>
                                            <Row>
                                                <Col md={4}>
                                                    <FormLabel>In column:</FormLabel>
                                                    <Field
                                                        name={"columns"}
                                                        as={"select"}
                                                        className={"form-control form-select"}
                                                        disabled={isSubmitting}
                                                    >
                                                        <option key={`columns-null`} value={""}>Select a column</option>
                                                        {this.state.columnsDescriptions
                                                            // Fix filter to search only visible columns
                                                            .filter(column => column.hidden !== true)
                                                            .map((column, i) => (
                                                            <option 
                                                                disabled={this.props.unsearchableColumns.includes(column.dataField)} 
                                                                key={this.props.tableName + "-" + i.toString()}
                                                                value={column.dataField}>
                                                                {StringFormatter.camelCaseToSpaceSeperatedString(
                                                                    column.dataField
                                                                )}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                    <ErrorMessage
                                                        name={"columns"}
                                                        component={"div"}
                                                        className={"field-error text-danger"}
                                                    />
                                                </Col>
                                                <Col md={2} lg={2}>
                                                    <FormLabel>Operator:</FormLabel>
                                                    <Field
                                                        name={"operator"}
                                                        as={"select"}
                                                        className={"form-control-lg form-select"}
                                                        disabled={isSubmitting}
                                                        size="lg"
                                                    >
                                                        <option key={`operator-null`} value={""}>Select a value</option>
                                                        <option key={`operator-like`} value={SearchBoxComparator.Like.toString()}>Contains</option>
                                                        <option key={`operator-strict-equal`} value={SearchBoxComparator.Equal.toString()}>Strict equal</option>
                                                    </Field>
                                                    <ErrorMessage
                                                        name={"operator"}
                                                        component={"div"}
                                                        className={"field-error text-danger"}
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <FormLabel>Find keywords:</FormLabel>
                                                    <Field
                                                        type={"text"}
                                                        name={"keywords"}
                                                        className={"form-control"}
                                                        disabled={isSubmitting}
                                                    />
                                                    <ErrorMessage
                                                        name={"keywords"}
                                                        component={"div"}
                                                        className={"field-error text-danger"}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Button
                                                        type={"submit"}
                                                        variant={"primary"}
                                                        style={{position: "inherit", bottom: "1rem", marginTop: "2rem"}}
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? "Searching ..." : "Search database"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </FormikForm>
                                    )}
                                </Formik>
                            </div>}
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {{
                        "loading": <p className={"text-primary"}>Loading column metadata ...</p>,
                        "error": <p className={"text-danger"}>
                            {"Error loading column metadata: " + this.state.columnsMetadataErrorMessage}
                        </p>,
                        "loaded": <></>
                    }[this.state.columnsMetadataStatus]}
                    {{
                        "loading": <p className={"text-primary"}>Loading table definition ...</p>,
                        "error": <p className={"text-danger"}>
                            {"Error loading table definition: " + this.state.tableDefinitionErrorMessage}
                        </p>,
                        "loaded": <></>
                    }[this.state.tableDefinitionStatus]}
                    {{
                        "loading": <p className={"text-primary"}>Loading table data ...</p>,
                        "error": <p className={"text-danger"}>
                            {"Error loading table data: " + this.state.tableDataErrorMessage}
                        </p>,
                        "loaded": <></>
                    }[this.state.tableDataStatus]}
                    {
                        this.state.columnsMetadataStatus === "loaded" &&
                        this.state.tableDefinitionStatus === "loaded" &&
                        this.state.tableDataStatus === "loaded" &&
                        this.state.tableData.length >= ROW_LIMIT[this.props.tableName] &&
                        <Alert variant={"warning"}>
                            <Alert.Heading>Warning!</Alert.Heading>
                            Too many results. Only display up to {ROW_LIMIT[this.props.tableName]} entries.
                        </Alert>
                    }
                    {
                        this.state.columnsMetadataStatus === "loaded" &&
                        this.state.tableDefinitionStatus === "loaded" &&
                        this.state.tableDataStatus === "loaded" &&
                        <TanStackTableWithUpdate
                            keyField={this.props.keyField}
                            data={this.state.tableData}
                            tableName={this.props.tableName}
                            columns={this.buildColumnDefinitions()}
                            rowClasses={this.props.rowClasses}
                            noDataComponent={
                                <h6 className={"text-primary"}>No data! Try to search within specific column?</h6>
                            }
                        />
                    }
                </Card.Body>
            </Card>
        </>
    }
}